import { graphql, useFragment } from "react-relay";
import {
  DatasetHelmetFragment$data,
  DatasetHelmetFragment$key,
} from "./__generated__/DatasetHelmetFragment.graphql";
import { LocationContextValue, useLocation } from "@/utils/location";
import { JsonValue, SafeScript } from "./SafeScript";
import { generatePath } from "react-router";
import { addIfDefined } from "@/utils/helpers";
import { License, resolveLicenseUrl } from "@/utils/license";
import { PropsWithChildren } from "react";
import { splitDatasetSlug } from "@/utils/routing";

const Fragment = graphql`
  fragment DatasetHelmetFragment on Dataset {
    name
    slug
    private
    shortDescription
    version: latestVersion {
      publishedAt
      updatedAt
      license
      authors {
        nodes {
          ... on User {
            displayName
          }
          ... on UnregisteredUser {
            displayName: fullName
          }
        }
      }
      files {
        nodes {
          id
          size
        }
      }
      doi {
        url
        full
      }
    }
    tags {
      nodes {
        name
      }
    }
    createdAt
  }
`;

interface Props extends PropsWithChildren {
  dataset: DatasetHelmetFragment$key;
}

export function DatasetHelmet(props: Props) {
  const location = useLocation();
  const dataset = useFragment(Fragment, props.dataset);

  return (
    <>
      <SafeScript type="application/ld+json">
        {formatDatasetSchema(dataset, location)}
      </SafeScript>
      {props.children}
    </>
  );
}

function formatDatasetSchema(
  dataset: DatasetHelmetFragment$data,
  location: LocationContextValue,
): JsonValue {
  const origin = location.origin;

  const datasetDescription = `${dataset.name}${
    dataset.shortDescription && dataset.shortDescription.length > 0
      ? `: ${dataset.shortDescription}`
      : ""
  }. A ${dataset.private ? "private" : "public"} dataset ready to download for algorithm development, QML workflows, and benchmarking.`;

  const breadcrumbList = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Datasets",
        item: generatePath(":origin/datasets", { origin }),
      },
      {
        "@type": "ListItem",
        position: 2,
        name: dataset.name,
        item: generatePath(":origin/datasets/:owner/:slug", {
          origin,
          ...splitDatasetSlug(dataset.slug),
        }),
      },
    ],
  };
  const datasetUrl = generatePath(":origin/datasets/:owner/:slug", {
    origin,
    ...splitDatasetSlug(dataset.slug),
  });

  const datasetSchema = {
    "@type": "Dataset",
    url: datasetUrl,
    inLanguage: "en",
    isAccessibleForFree: true,
    name: dataset.name,
    description: datasetDescription,
    keywords: [
      "quantum computing",
      "quantum dataset",
      "quantum machine learning",
      "benchmark",
      ...(dataset.tags.nodes.length > 0
        ? dataset.tags.nodes.map(({ name }) => name)
        : []),
    ],
    ...addIfDefined(
      "datePublished",
      dataset.version && dataset.version.publishedAt
        ? new Date(dataset.version.publishedAt).toISOString()
        : undefined,
    ),
    ...addIfDefined(
      "dateModified",
      dataset.version && dataset.version.updatedAt
        ? new Date(dataset.version.updatedAt).toISOString()
        : undefined,
    ),
    ...addIfDefined(
      "license",
      dataset.version && dataset.version.license
        ? resolveLicenseUrl(dataset.version.license as License).toString()
        : undefined,
    ),
    ...addIfDefined(
      "creator",
      dataset.version && dataset.version.authors
        ? dataset.version.authors.nodes.map(({ displayName }) => ({
            "@type": "Person",
            name: displayName,
          }))
        : undefined,
    ),
    ...addIfDefined(
      "distribution",
      dataset.version && dataset.version.files
        ? dataset.version.files.nodes.map(({ id, size }) => ({
            "@type": "DataDownload",
            name: id,
            contentUrl: generatePath(":origin/file/:id", { origin, id }),
            encodingFormat: "application/x-parquet",
            contentSize: size,
          }))
        : undefined,
    ),
    ...addIfDefined(
      "identifier",
      dataset.version && dataset.version.doi
        ? dataset.version.doi.full
        : undefined,
    ),
    ...addIfDefined(
      "sameAs",
      dataset.version && dataset.version.doi
        ? dataset.version.doi.url
        : undefined,
    ),

    includedInDataCatalog: {
      "@type": "DataCatalog",
      name: "Aqora",
      url: generatePath(":origin/datasets", { origin }),
    },
  };

  return {
    "@context": "https://schema.org",
    url: datasetUrl,
    "@graph": {
      dataset: datasetSchema,
      breadcrumb: breadcrumbList,
    },
  };
}
